import { search } from './searchService';
import { get, getById, put, post, patch, del } from './apiService';
import type { MysteryShopCall, CallFilter } from '../types/mysteryShop';
import type { CallFilters } from '../types/callFilter';
import type { SearchParams } from '../types/search';

export async function listCalls(
  filter: CallFilters = {},
  params: SearchParams = {},
): Promise<MysteryShopCall[]> {
  return search<MysteryShopCall>('/retell/admin/calls', filter, params);
}

export async function getCallById(callId: string): Promise<MysteryShopCall> {
  return getById<MysteryShopCall>('/retell-call', callId, 'expand=events,retellCallReport');
}

export async function associateProperty(callId: string, propertyId: string): Promise<MysteryShopCall> {
  return put<MysteryShopCall>(`/retell/admin/call/${callId}/property/${propertyId}`, {});
}

export async function associateAgent(callId: string, agentId: string, propertyId: string): Promise<MysteryShopCall> {
  return post<MysteryShopCall>(`/retell/admin/call/${callId}/agent/${agentId}`, { propertyId });
}

export async function createAndAssociateAgent(callId: string, name: string, propertyId: string, profileId?: string): Promise<MysteryShopCall> {
  const qs = profileId ? `?profileId=${profileId}` : '';
  return post<MysteryShopCall>(`/retell/admin/call/${callId}/agent${qs}`, { name, propertyId });
}

export async function associateCallReport(callId: string, retellCallReportId: string): Promise<MysteryShopCall> {
  return put<MysteryShopCall>(`/retell/admin/call/${callId}/retell-call-report/${retellCallReportId}`, {});
}

export async function publishCall(callId: string, published: boolean): Promise<unknown> {
  return put(`/retell/agent/call/${callId}/status?published=${published}`, {});
}

export async function emailCall(callId: string, email: string): Promise<unknown> {
  return put(`/retell/agent/call/${callId}/status?emailed=true&email=${encodeURIComponent(email)}`, {});
}

export async function rescoreCall(callId: string): Promise<unknown> {
  return get(`/retell/admin/call/${callId}/rescore`);
}

export async function setDoNotCount(callId: string, doNotCount: boolean): Promise<MysteryShopCall> {
  return patch<MysteryShopCall>(`/retell/admin/call/${callId}/do-not-count`, { doNotCount });
}

export interface AutoFillResponse {
  propertyId: string;
  propertyName: string;
  retellAgentId: string;
  fromNumber: string;
  fromNumberCollision: boolean;
  voiceId: string;
  voiceCollision: boolean;
  retellCallReportId: string;
  timezone: string;
  inputVars: Record<string, string>;
}

export async function autoFill(clientId: string, propertyId?: string): Promise<AutoFillResponse> {
  return post<AutoFillResponse>('/retell/admin/call/auto-fill', { clientId, ...(propertyId && { propertyId }) });
}

export async function patchScheduledCall(callId: string, data: Record<string, unknown>): Promise<MysteryShopCall> {
  return patch<MysteryShopCall>(`/retell/admin/call/${callId}/scheduled`, data);
}

export async function cancelScheduledCall(callId: string): Promise<void> {
  return del(`/retell-call/${callId}`);
}

export interface CollisionResult {
  date: string;
  propertyName: string | null;
  fromNumber: string;
  agentName: string | null;
}

export interface CollisionResponse {
  fromNumberCollisions: CollisionResult[];
  agentNameCollisions: CollisionResult[];
  voiceCollisions: CollisionResult[];
  emailCollisions: CollisionResult[];
}

export async function checkCollision(data: { clientId: string; propertyId: string; fromNumber: string; agentName: string; voiceId?: string; email?: string }): Promise<CollisionResponse> {
  return post<CollisionResponse>('/retell/admin/call/collision-check', data);
}

export async function setDoesCountReason(callId: string, doesCountReason: string | null): Promise<MysteryShopCall> {
  return patch<MysteryShopCall>(`/retell/admin/call/${callId}/does-count-reason`, { doesCountReason });
}
